import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiArrowsClockwiseLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import GlassCard from '../brand/GlassCard';
import { Well } from '../brand/Field';
import Badge from '../TailwindComponents/Badge';
import Button from '../TailwindComponents/Button';
import Skeleton from '../TailwindComponents/Skeleton';
import Alert from '../TailwindComponents/Alert';
import EmptyState from '../TailwindComponents/EmptyState';
import ProfileImage from '../../Header/ProfileImage';
import { feedTimeLabel } from '../feed/feedTime';
import { ActivityEntry, actorInitials } from './activityModel';
import { activityBadgeColor, activityGlyph } from './activityPresentation';

export interface ActivityPaneProps {
  /** Already normalized - see `activityModel`. The caller owns fetching. */
  entries: ActivityEntry[];
  loading?: boolean;
  error?: string;
  onRefresh: () => Promise<void> | void;
  /** Shown when there is nothing to display. Defaults to a muted line. */
  emptyState?: React.ReactNode;
  /**
   * 'canvas' - the pane IS the surface: one glass card per entry.
   * 'pane'   - nested inside a card or a surface pane: entries are sunken
   *            wells instead, because a glass card inside a glass card is a
   *            card in a card.
   */
  density?: 'canvas' | 'pane';
  /** Optional: clicking an entry does something (jump to the finding it names). */
  onEntryClick?: (entry: ActivityEntry) => void;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void> | void;
  /** Hide the refresh row entirely - for hosts that own their own toolbar. */
  hideToolbar?: boolean;
  /**
   * Show the actor's avatar beside their name.
   *
   * Off by default: most audit rows are attributed to a name the host cannot
   * resolve to a person, and a column of identical initials discs is noise.
   * Hosts that DO hold a people directory (the administration dashboard) turn
   * it on and fill `entry.actorEmail`, which is what makes the disc a face.
   */
  showActorAvatar?: boolean;
  className?: string;
}

interface ActivityItemProps {
  entry: ActivityEntry;
  density: 'canvas' | 'pane';
  showActorAvatar?: boolean;
  onClick?: (entry: ActivityEntry) => void;
}

/**
 * One row on the activity rail - the same anatomy as `feed/FeedEventItem`:
 * a hairline running down the list with a glyph disc sitting on it, and the
 * record itself as a quiet surface beside it. An activity log and a feed are
 * both chronologies of the same work, so they read as one family.
 */
const ActivityItem: React.FC<ActivityItemProps> = ({
  entry,
  density,
  showActorAvatar = false,
  onClick,
}) => {
  const { t } = useTranslation();
  const time = feedTimeLabel(entry.timestamp, t);
  const clickable = !!onClick;

  const body = (
    <>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Badge color={activityBadgeColor(entry.kind)} className="text-[10px] flex-shrink-0">
          {t(entry.action)}
        </Badge>
        {entry.target && (
          <span className="min-w-0 break-words text-xs text-fg-body">{entry.target}</span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-fg-muted">
        {entry.actor && (
          <span className="flex min-w-0 items-center gap-1.5">
            {showActorAvatar && (
              <ProfileImage
                /*
                  The picture, when the host knows it. This was a hardcoded
                  `null`, which meant the avatar could never get past
                  `ProfileImage`'s second choice: every row on a surface that
                  DOES know whose activity it is - the dashboard feed is the
                  signed-in user's own - drew Gravatar or initials while the
                  real picture sat unused in the store.
                */
                profilePicture={entry.actorAvatar ?? null}
                email={entry.actorEmail ?? null}
                initials={actorInitials(entry.actor)}
                name={entry.actor}
                size="w-5 h-5"
              />
            )}
            <span className="truncate font-medium text-fg-body">{entry.actor}</span>
          </span>
        )}
        {time.label && <span title={time.title}>{time.label}</span>}
      </div>
    </>
  );

  return (
    <li className="relative ps-9">
      {/* The glyph disc that sits on the rail. */}
      <span
        aria-hidden
        className="absolute start-0 top-2.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-hairline bg-surface text-fg-muted [&>svg]:h-3.5 [&>svg]:w-3.5"
      >
        {activityGlyph(entry.kind)}
      </span>

      {density === 'pane' ? (
        <Well
          className={clsx(
            'p-2.5',
            clickable && 'cursor-pointer transition-colors hover:bg-surface-soft'
          )}
          onClick={clickable ? () => onClick!(entry) : undefined}
        >
          {body}
        </Well>
      ) : (
        <GlassCard
          padding="sm"
          className={clsx(clickable && 'cursor-pointer transition-shadow hover:shadow-md')}
        >
          <div onClick={clickable ? () => onClick!(entry) : undefined}>{body}</div>
        </GlassCard>
      )}
    </li>
  );
};

/**
 * THE activity/audit timeline. One shared surface behind the audit log panel,
 * the per-page activity panes and the recent-activity cards.
 *
 * It owns its own scrolling with `flex-1 min-h-0 overflow-y-auto` and never
 * measures the viewport: a `calc(100vh - 180px)` height is a guess about how
 * much chrome sits above the list, and it is wrong in every docked pane, every
 * slide-over and every short viewport.
 *
 * Timestamps go through `feed/feedTime` - relative, translated, absolute on
 * hover - so an entry is aged the same way here as it is on the feed.
 */
const ActivityPane: React.FC<ActivityPaneProps> = ({
  entries,
  loading = false,
  error = '',
  onRefresh,
  emptyState,
  density = 'canvas',
  onEntryClick,
  hasMore = false,
  onLoadMore,
  hideToolbar = false,
  showActorAvatar = false,
  className,
}) => {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  const handleLoadMore = useCallback(async () => {
    if (!onLoadMore) return;
    setLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setLoadingMore(false);
    }
  }, [onLoadMore]);

  if (error) {
    return (
      <div className={clsx('p-3', className)}>
        <Alert type="error" message={error} />
      </div>
    );
  }

  const showSkeleton = loading && entries.length === 0;

  return (
    <div className={clsx('flex h-full min-h-0 w-full flex-col gap-2', className)}>
      {!hideToolbar && (
        <div className="flex flex-shrink-0 items-center justify-end">
          <Button
            variant="outline"
            size="small"
            onClick={handleRefresh}
            disabled={loading}
            isLoading={refreshing}
            ariaLabel={t('Refresh')}
            icon={<PiArrowsClockwiseLight className="h-4 w-4" />}
          >
            {t('Refresh')}
          </Button>
        </div>
      )}

      {/*
        The empty state sits in the MIDDLE of the pane.

        This box is `flex-1`, so with nothing in it the "nothing recorded yet"
        block was pinned to the top edge with the whole pane empty below it —
        reading as a list that had failed to load rather than as a list with
        nothing in it. When there is nothing to scroll, the box centres what it
        holds; a list still starts at the top, where a list belongs.
      */}
      <div
        className={clsx(
          'custom-scrollbar-main min-h-0 flex-1 overflow-y-auto overflow-x-hidden',
          !showSkeleton && entries.length === 0 && 'flex flex-col items-center justify-center',
        )}
      >
        {showSkeleton ? (
          <Skeleton variant="list" rows={4} />
        ) : entries.length === 0 ? (
          // A caller that hands over a whole node owns the empty state; a
          // caller that hands over a line of text gets THE empty state around
          // it, rather than this pane's own centered div.
          React.isValidElement(emptyState) ? (
            emptyState
          ) : (
            <EmptyState
              density="compact"
              icon={<PiClockCounterClockwiseLight />}
              headline={t('Nothing recorded yet')}
              subtext={
                typeof emptyState === 'string'
                  ? emptyState
                  : t('Activity appears here as work is done.')
              }
            />
          )
        ) : (
          <>
            <ol className="relative space-y-2.5 py-1">
              {/* The rail the glyph discs sit on. */}
              <span aria-hidden className="absolute start-3 top-2 bottom-2 w-px bg-hairline-soft" />
              {entries.map((entry, idx) => (
                <ActivityItem
                  key={entry.id || `activity-${idx}`}
                  entry={entry}
                  density={density}
                  showActorAvatar={showActorAvatar}
                  onClick={onEntryClick}
                />
              ))}
            </ol>

            {hasMore && onLoadMore && (
              <div className="flex justify-center pt-3">
                <Button
                  variant="outline"
                  size="small"
                  onClick={handleLoadMore}
                  isLoading={loadingMore}
                >
                  {t('Load more')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityPane;
